import { ReactElement, cloneElement, useState } from 'react';
import { Box, Divider, InputAdornment, ListItemIcon, ListItemText, Menu, MenuItem, TextField } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

export interface DropdownMenuItem {
    id: string;
    label: string;
    icon?: React.ReactNode;
    onClick: () => void;
    disabled?: boolean;
    divider?: boolean;
    hidden?: boolean;
    /** A second line under the label — e.g. why a disabled item is disabled,
     *  which a tooltip cannot say (a disabled item takes no pointer events). */
    hint?: string;
}

export interface DropdownMenuProps {
    // Menu items
    items: DropdownMenuItem[];

    // Custom trigger (optional)
    /** If provided, uses this element as the dropdown trigger instead of default TextField */
    trigger?: ReactElement;

    // Default trigger configuration (used when trigger is NOT provided)
    /** Label for the default TextField trigger */
    label?: string;
    /** Placeholder text when no value is selected */
    placeholder?: string;
    /** Currently selected value to display in default trigger */
    value?: string;
    /** Icon to show at start of default TextField */
    startIcon?: React.ReactNode;
    /** Make default TextField full width */
    fullWidth?: boolean;

    // Common props
    disabled?: boolean;
    onOpen?: () => void;
    onClose?: () => void;
    className?: string;
    menuPosition?: 'left' | 'right';
    minWidth?: number | string;
}

/**
 * Generic dropdown menu component with flexible trigger options
 *
 * BEHAVIOR:
 * 1. If `trigger` prop is provided → Uses your custom element as trigger
 * 2. If `trigger` is NOT provided → Renders as MUI TextField (Select-like appearance)
 *
 * DEFAULT APPEARANCE (no trigger prop):
 * - Looks like MUI Select/Autocomplete
 * - Uses TextField with readonly input
 * - Shows arrow icon on the right
 * - Perfect for filters, selectors (location, status, etc.)
 *
 * CUSTOM TRIGGER (trigger prop provided):
 * - Uses your custom element (CustomButton, IconButton, div, etc.)
 * - Perfect for action menus, create buttons, etc.
 *
 * @example
 * // Default MUI Select-like (for location selector)
 * <DropdownMenu
 *   label="Location"
 *   placeholder="Select location"
 *   value={selectedLocation}
 *   items={locationItems}
 * />
 *
 * @example
 * // Custom trigger (for create booking button)
 * <DropdownMenu
 *   trigger={<CustomButton title="Create Booking" isFilled />}
 *   items={bookingItems}
 * />
 *
 * @example
 * // Custom trigger (for icon actions menu)
 * <DropdownMenu
 *   trigger={<IconButton><MoreVertIcon /></IconButton>}
 *   items={actionItems}
 * />
 */
const DropdownMenu: React.FC<DropdownMenuProps> = ({
    items,
    trigger,
    label,
    placeholder,
    value,
    startIcon,
    fullWidth = false,
    disabled = false,
    onOpen,
    onClose,
    className,
    menuPosition = 'right',
    minWidth = 'auto',
}) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
        if (disabled) return;
        setAnchorEl(event.currentTarget);
        onOpen?.();
    };

    const handleClose = () => {
        setAnchorEl(null);
        onClose?.();
    };

    const handleItemClick = (item: DropdownMenuItem) => {
        if (item.disabled) return;
        item.onClick();
        handleClose();
    };

    // Filter out hidden items
    const visibleItems = items.filter((item) => !item.hidden);

    if (visibleItems.length === 0) {
        return null;
    }

    // Render trigger element
    const renderTrigger = () => {
        // CASE 1: Custom trigger provided → Use it
        if (trigger) {
            return cloneElement(trigger, {
                onClick: handleOpen,
                disabled: disabled,
                'aria-haspopup': 'menu',
                'aria-expanded': open,
                style: {
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    ...trigger.props.style,
                },
            } as any);
        }

        // CASE 2: No custom trigger → Render default MUI TextField (Select-like)
        return (
            <TextField
                label={label}
                value={value || ''}
                placeholder={placeholder}
                onClick={handleOpen}
                disabled={disabled}
                fullWidth={fullWidth}
                InputProps={{
                    readOnly: true,
                    startAdornment: startIcon ? <InputAdornment position={'start'}>{startIcon}</InputAdornment> : undefined,
                    endAdornment: (
                        <InputAdornment position={'end'}>
                            <KeyboardArrowDownIcon />
                        </InputAdornment>
                    ),
                }}
                sx={{
                    '& .MuiInputBase-input': {
                        cursor: disabled ? 'not-allowed' : 'pointer',
                    },
                    '& .MuiOutlinedInput-root': {
                        backgroundColor: 'background.paper',
                    },
                }}
            />
        );
    };

    return (
        <Box className={className} sx={{ width: fullWidth ? '100%' : 'auto' }}>
            {renderTrigger()}
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: menuPosition,
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: menuPosition,
                }}
                sx={{
                    '& .MuiPaper-root': {
                        marginTop: '4px',
                        minWidth: minWidth === 'auto' ? anchorEl?.offsetWidth || 'auto' : minWidth,
                    },
                }}
            >
                {/* Flat children: MenuList moves focus (autofocus, arrow keys)
                    between its DIRECT children, so a wrapper element around each
                    item made every item unreachable from the keyboard. */}
                {visibleItems.flatMap((item, index) => {
                    const entry = (
                        // No colour for destructive items: they are told apart by
                        // position — last, behind a divider — not by red
                        // (grayscale rule; design-decisions.md › Zeilenaktionen).
                        <MenuItem
                            key={item.id}
                            onClick={() => handleItemClick(item)}
                            disabled={item.disabled}
                        >
                            {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
                            <ListItemText secondary={item.hint}>{item.label}</ListItemText>
                        </MenuItem>
                    );
                    return item.divider && index < visibleItems.length - 1
                        ? [entry, <Divider key={`${item.id}-divider`} />]
                        : [entry];
                })}
            </Menu>
        </Box>
    );
};

export default DropdownMenu;
